// SCR-STAFF-801 (AP-CMP-02؛ US-CMP-024؛ AC-CMP-024.1): شبیه‌ساز قرارداد — اثر یک قاعده پیشنهادی (درصد، ثابت یا پلکانی، با مبنای دلخواه و کسر
// هزینه) روی کارکرد واقعی یک بازه، در کنار سهم فعلی؛ هیچ ردیفی ثبت نمی‌شود (C-AGENT-1388).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import {
  unwrap,
  type CmpShareBasis,
  type CmpShareMethod,
  type CmpSimulationRequest,
  type CmpSimulationRow,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  TextFieldRHF,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { parseTiers, ProviderPicker, RULE_BASES, RULE_METHODS, toBasisPoints } from './common';

export const SCR_STAFF_801 = 'SCR-STAFF-801';

const schema = z
  .object({
    providerId: z
      .string()
      .nullable()
      .refine((value) => Boolean(value), { error: 'common:validation.required' }),
    from: z.string().min(1, { error: 'common:validation.required' }),
    to: z.string().min(1, { error: 'common:validation.required' }),
    basis: z.string(),
    method: z.string(),
    rate: z.string(),
    amount: z.number().nullable(),
    tiers: z.string(),
    cardFee: z.string(),
  })
  .superRefine((form, ctx) => {
    if (
      form.method === 'Percent' &&
      !(Number(form.rate) >= 0 && Number(form.rate) <= 100 && form.rate.trim() !== '')
    ) {
      ctx.addIssue({ code: 'custom', path: ['rate'], message: 'cmp:simulator.rate_invalid' });
    }
    if (
      (form.method === 'FixedPerCase' || form.method === 'FixedPerUnit') &&
      !(form.amount && form.amount > 0)
    ) {
      ctx.addIssue({ code: 'custom', path: ['amount'], message: 'common:validation.required' });
    }
    if (form.method === 'Tiered' && !parseTiers(form.tiers)) {
      ctx.addIssue({ code: 'custom', path: ['tiers'], message: 'cmp:form.rule_tiers_invalid' });
    }
  });
type SimulatorForm = z.input<typeof schema>;

export function SimulatorPage() {
  const { t } = useTranslation('cmp');
  const { tenantApi } = useTenantSession();
  const { control, handleSubmit } = useForm<SimulatorForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      providerId: null,
      from: '',
      to: '',
      basis: 'NetOfDiscount',
      method: 'Percent',
      rate: '',
      amount: null,
      tiers: '',
      cardFee: '',
    },
  });
  const method = useWatch({ control, name: 'method' });

  const simulate = useMutation({
    mutationFn: (form: SimulatorForm) => {
      const fixed = form.method === 'FixedPerCase' || form.method === 'FixedPerUnit';
      const body: CmpSimulationRequest = {
        providerId: form.providerId ?? '',
        from: form.from,
        to: form.to,
        cardFeeBasisPoints: form.cardFee.trim() ? toBasisPoints(Number(form.cardFee)) : null,
        rules: [
          {
            role: 'Primary',
            scope: 'All',
            scopeId: null,
            basis: form.basis as CmpShareBasis,
            method: form.method as CmpShareMethod,
            rateBasisPoints: form.method === 'Percent' ? toBasisPoints(Number(form.rate)) : null,
            amount: fixed ? { amount: form.amount ?? 0, currency: 'IRR' } : null,
            tiers:
              form.method === 'Tiered'
                ? (parseTiers(form.tiers) ?? []).map((tier) => ({
                    from: { amount: tier.from, currency: 'IRR' },
                    rateBasisPoints: tier.rateBasisPoints,
                  }))
                : [],
            costDeductions: [],
            commissionSource: null,
          },
        ],
      };
      return unwrap(tenantApi.POST('/api/v1/compensation/agreements:simulate', { body }));
    },
  });

  const columns: DataTableColumn<CmpSimulationRow>[] = [
    {
      id: 'service',
      headerKey: 'cmp:simulator.columns.service',
      cell: (row) => row.serviceName ?? t('cmp:report.total'),
    },
    {
      id: 'lines',
      headerKey: 'cmp:simulator.columns.lines',
      cell: (row) => faDigits(Number(row.lines)),
      align: 'right',
    },
    {
      id: 'base',
      headerKey: 'cmp:simulator.columns.base',
      cell: (row) => <Money amountMinor={Number(row.base.amount)} />,
      align: 'right',
    },
    {
      id: 'current',
      headerKey: 'cmp:simulator.columns.current',
      cell: (row) => <Money amountMinor={Number(row.current.amount)} />,
      align: 'right',
    },
    {
      id: 'proposed',
      headerKey: 'cmp:simulator.columns.proposed',
      cell: (row) => <Money amountMinor={Number(row.proposed.amount)} />,
      align: 'right',
    },
    {
      id: 'difference',
      headerKey: 'cmp:simulator.columns.difference',
      cell: (row) => (
        <span data-testid={`simulation-difference-${row.serviceId ?? 'total'}`}>
          <Money amountMinor={Number(row.difference.amount)} />
        </span>
      ),
      align: 'right',
    },
  ];
  const rows = simulate.data ? [...simulate.data.rows, simulate.data.total] : undefined;

  return (
    <ListPage screenId={SCR_STAFF_801} titleKey="cmp:simulator.title" subtitleKey="cmp:simulator.subtitle">
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          spacing={2}
          component="form"
          onSubmit={(event) => void handleSubmit((form) => simulate.mutate(form))(event)}
          noValidate
        >
          <Controller
            control={control}
            name="providerId"
            render={({ field, fieldState }) => (
              <ProviderPicker
                value={field.value ?? null}
                onChange={field.onChange}
                labelKey="cmp:simulator.provider"
                testId="simulator-provider"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <JalaliDatePickerRHF control={control} name="from" labelKey="cmp:simulator.from" required />
            <JalaliDatePickerRHF control={control} name="to" labelKey="cmp:simulator.to" required />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <SelectRHF
              control={control}
              name="basis"
              labelKey="cmp:form.rule_basis"
              fullWidth
              options={RULE_BASES.map((value) => ({ value, labelKey: `cmp:share_basis.${value}` }))}
            />
            <SelectRHF
              control={control}
              name="method"
              labelKey="cmp:form.rule_method"
              fullWidth
              options={RULE_METHODS.map((value) => ({ value, labelKey: `cmp:method.${value}` }))}
            />
            {method === 'Percent' && (
              <TextFieldRHF
                control={control}
                name="rate"
                labelKey="cmp:form.rule_rate"
                fullWidth
                slotProps={{ htmlInput: { inputMode: 'decimal', 'data-testid': 'simulator-rate' } }}
              />
            )}
            {(method === 'FixedPerCase' || method === 'FixedPerUnit') && (
              <MoneyFieldRHF control={control} name="amount" labelKey="cmp:form.rule_amount" fullWidth />
            )}
            <TextFieldRHF
              control={control}
              name="cardFee"
              labelKey="cmp:form.card_fee"
              fullWidth
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
          </Stack>
          {method === 'Tiered' && (
            <TextFieldRHF
              control={control}
              name="tiers"
              labelKey="cmp:form.rule_tiers"
              helperKey="cmp:form.rule_tiers_help"
              fullWidth
              slotProps={{ htmlInput: { 'data-testid': 'simulator-tiers' } }}
            />
          )}
          <Alert severity="info">{t('cmp:simulator.note')}</Alert>
          <Stack direction="row">
            <Button
              type="submit"
              variant="contained"
              disabled={simulate.isPending}
              data-testid="simulator-run"
            >
              {t('cmp:simulator.run')}
            </Button>
          </Stack>
          {simulate.error ? <ErrorState error={simulate.error} /> : null}
        </Stack>
      </Paper>
      {rows && (
        <DataTable
          captionKey="cmp:simulator.caption"
          columns={columns}
          rows={rows}
          getRowId={(row) => row.serviceId ?? 'total'}
          emptyTitleKey="cmp:simulator.empty"
        />
      )}
    </ListPage>
  );
}
